/**
 * Persistent key/value storage for app state.
 * Uses @capacitor/preferences on mobile, falls back to localStorage on web.
 *
 * NOT for credentials on native: @capacitor/preferences is a plaintext
 * SharedPreferences / UserDefaults file, not the Keystore. The native auth
 * token lives in the Keystore-backed vault (native-token-vault.ts,
 * auth-token-persistence.ts; P1-12) and is excluded from the persisted
 * auth blob that goes through this service.
 */
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

const isNative = Capacitor.isNativePlatform();

class SecureStorageService {
  async get(key: string): Promise<string | null> {
    if (isNative) {
      try {
        const { value } = await Preferences.get({ key });
        return value;
      } catch {
        return null;
      }
    }
    return localStorage.getItem(key);
  }

  async set(key: string, value: string): Promise<void> {
    if (isNative) {
      try {
        await Preferences.set({ key, value });
      } catch {
        // Fall back to localStorage if Preferences fails
        localStorage.setItem(key, value);
      }
    } else {
      localStorage.setItem(key, value);
    }
  }

  async remove(key: string): Promise<void> {
    if (isNative) {
      try {
        await Preferences.remove({ key });
      } catch {
        // Best-effort removal
      }
    } else {
      localStorage.removeItem(key);
    }
  }

  /**
   * Synchronous get -- only works on web (localStorage). On native, returns null.
   * Use this ONLY for non-sensitive data where async isn't practical (e.g. Zustand persist).
   */
  getSync(key: string): string | null {
    return localStorage.getItem(key);
  }

  /**
   * Synchronous set -- writes to localStorage immediately, and also mirrors the
   * write to Capacitor Preferences on native for encrypted-at-rest consistency.
   */
  setSync(key: string, value: string): void {
    localStorage.setItem(key, value);
    // Also write to Preferences on native for consistency
    if (isNative) {
      Preferences.set({ key, value }).catch(() => {});
    }
  }

  /**
   * Synchronous remove -- removes from localStorage immediately, and also mirrors
   * the removal to Capacitor Preferences on native.
   */
  removeSync(key: string): void {
    localStorage.removeItem(key);
    if (isNative) {
      Preferences.remove({ key }).catch(() => {});
    }
  }
}

export const secureStorage = new SecureStorageService();

// ---------------------------------------------------------------------------
// Zustand persist storage adapter
// ---------------------------------------------------------------------------
import type { StateStorage } from 'zustand/middleware';

/**
 * Drop-in replacement for the default localStorage-backed Zustand persist storage.
 * Uses secureStorage sync methods so that on native platforms, writes are mirrored
 * to Capacitor Preferences (encrypted Keychain/Keystore).
 *
 * Usage:
 * ```ts
 * persist(storeCreator, {
 *   name: 'my-store',
 *   storage: createJSONStorage(() => secureZustandStorage),
 * })
 * ```
 */
export const secureZustandStorage: StateStorage = {
  getItem: (name: string) => secureStorage.getSync(name),
  setItem: (name: string, value: string) => secureStorage.setSync(name, value),
  removeItem: (name: string) => secureStorage.removeSync(name),
};
